<!-- /////////////////////////////////////////////////////////////////////////// TEMPLATE -->

<template>

    <main-layout>

        <!-- Loaded -->
        <div v-if="!pageLoading" class="sl-page">

            <!-- Page Header -->
            <div class="sl-page__header flex justify-between">
                <!-- Page Title -->
                <div class="sl-page__title">
                    {{ $t('returns.title') }}
                </div>
            </div>

            <!-- table tools -->
            <div class="sl-page__tools">
                <!-- Search Field -->
                <div class="sl-search">
                    <mdi-icon icon="mdiMagnify" />

                    <input
                        ref="searchField"
                        v-model="searchText"
                        type="text"
                        class="sl-search__field"
                        @input="searchField_OnInput"
                    >
                </div>

                <div class="grow"></div>

                <!-- Pagination -->
                <div v-if="!searching && returns.length" class="pagination">
                    <!-- Results per Page -->
                    <tippy>
                        <template #content>
                            {{ $t('pagination.results_per_page.label') }}
                        </template>

                        <div class="pagination__page-size relative">
                            <span>{{ pageSize }}</span>

                            <mdi-icon icon="mdiMenuDown" />

                            <select v-model="pageSize">
                                <option
                                    v-for="value in pageSizes"
                                    :key="`ps${value}`"
                                    :value="value"
                                >
                                    {{ value }}
                                </option>
                            </select>
                        </div>
                    </tippy>

                    <!-- Previous Button -->
                    <div v-if="cPage !== 1" class="pagination__page" @click="previous">
                        &lt;
                    </div>

                    <!-- Pages -->
                    <template v-for="index in pages">
                        <div
                            v-if="typeof index === 'number'" :key="`a${index}`"
                            class="pagination__page"
                            :class="{ 'pagination__page--active' : index === cPage }"
                            @click="setCurrentPage(index)"
                        >
                            {{ index }}
                        </div>

                        <div v-else :key="`b${index}`" class="pagination__spacer">
                            {{ index }}
                        </div>
                    </template>

                    <!-- Next Button -->
                    <div v-if="cPage !== totalPages" class="pagination__page" @click="next">
                        &gt;
                    </div>
                </div>
            </div>

            <!-- Page Content -->
            <div v-if="!resultsLoading" class="sl-page__content">
                <template v-if="returns.length">
                    <!-- Return Table -->
                    <sl-table
                        :columns="columns"
                        :rows="returns"
                        @remove="remove"
                    />
                </template>
                <template v-else>
                    <div class="text-gray-500 text-sm mt-4 text-left">
                        {{ $t('common.search.no_result') }}
                    </div>
                </template>
            </div>

            <div v-else class="mt-8 text-center">
                <img class="inline" src="/img/loader-5.gif">
            </div>

        </div>

        <!-- Page Loading -->
        <template v-else>
            <div class="mt-8 mx-auto">
                <img class="inline" src="/img/loader-5.gif">
            </div>
        </template>

    </main-layout>

</template>

<!-- /////////////////////////////////////////////////////////////////////////// SCRIPT -->

<script>

import { markRaw } from 'vue';
import { debounce } from 'lodash-es';
import { useToast } from 'vue-toastification';
import MainLayout   from '@/layouts/MainLayout.vue';
import SlTable from '@/components/sl-table/SlTable.vue';
import LensReturnTableDetails from '@/components/return/LensReturnTableDetails.vue';
import ReturnOrderIdentificationCell from '@/components/sl-table/sl-cells/ReturnOrderIdentificationCell.vue';
import ReturnDetailsCell from '@/components/sl-table/sl-cells/ReturnDetailsCell.vue';
import ReturnCreditCell from '@/components/sl-table/sl-cells/ReturnCreditCell.vue';
import ReturnOrderPriceCell from '@/components/sl-table/sl-cells/ReturnOrderPriceCell.vue';

export default
{
    components:
    {
        MainLayout,
        SlTable,
    },

    data()
    {
        const currency_code = this.$store.state.returns.set[0]?.order.currency_code;

        return {
            pageLoading:    true,
            resultsLoading: false,
            searching:      false,
            searchText:     '',
            pageSize:       10,
            cPage:          1,

            // SL Table definition
            columns: [
                {
                    key:        'rdate',
                    tlabel:     'return_table.headers.date',
                    formatters: 'datef:DD.MM.YYYY',
                    align:      'center',
                    cssClass:   'text-gray-400 text-xs',
                },
                {
                    key:           'order_reference',
                    tlabel:        'return_table.headers.reference',
                    cellComponent: markRaw(ReturnOrderIdentificationCell),
                },
                {
                    key:           'article',
                    tlabel:        'return_table.headers.article',
                    cellComponent: markRaw(ReturnDetailsCell),
                },
                {
                    key:      'return_reason_code',
                    tlabel:   'return_table.headers.return_reason',
                    align:    'center',
                    cssClass: 'text-sm text-gray-600',
                    format:   val =>
                    {
                        return this.$t(`return_reason.${val}.label`);
                    },
                },
                {
                    tlabel:        `return_table.headers.order_price`,
                    align:         'right',
                    unit:          currency_code,
                    cellComponent: markRaw(ReturnOrderPriceCell),
                },
                {
                    tlabel:        `return_table.headers.credit`,
                    align:         'right',
                    unit:          currency_code,
                    cellComponent: markRaw(ReturnCreditCell),
                },
            ],
        };
    },

    computed:
    {
        pageSizes()
        {
            return [10, 25, 50];
        },

        returns()
        {
            const returns = this.$store.state.returns.set;

            for(const ret of returns)
            {
                ret.details_component = (ret.order.order_class == 'App\\Orders\\Orders\\LensOrder') ? markRaw(LensReturnTableDetails) : null;
            }

            return returns;
        },

        total()
        {
            return this.$store.state.returns.total;
        },

        totalPages()
        {
            return Math.ceil(this.total / this.pageSize) || 0;
        },

        pages()
        {
            if(!this.total)
            {
                return [];
            }

            const pages = [];

            if(this.totalPages <= 5)
            {
                for(let i = 1; i <= this.totalPages; i += 1)
                {
                    pages.push(i);
                }
            }
            else
            {
                let start = this.cPage - 1;
                let end = this.cPage + 1;

                if(start < 2)
                {
                    const delta = Math.abs(2 - start);
                    start += delta;
                    end   += delta;
                }

                if(end > (this.totalPages - 1))
                {
                    const delta = Math.abs(end - (this.totalPages - 1));
                    start -= delta;
                    end   -= delta;
                }

                pages.push(1);

                if(start > 2)
                {
                    pages.push('…');
                }

                for(let i = start; i <= end; i += 1)
                {
                    pages.push(i);
                }

                if(end < (this.totalPages - 1))
                {
                    pages.push('…');
                }

                if(end < this.totalPages)
                {
                    pages.push(this.totalPages);
                }
            }

            return pages;
        },
    },

    watch:
    {
        pageSize()
        {
            this.searchReturns();
        },
    },

    mounted()
    {
        this.searchReturns()
            .then(() =>
            {
                this.pageLoading = false;

                this.$nextTick(() => this.$refs.searchField?.focus());
            });
    },

    methods:
    {
        previous()
        {
            this.setCurrentPage(this.cPage - 1);
        },

        next()
        {
            this.setCurrentPage(this.cPage + 1);
        },

        setCurrentPage(pageNumber, forceSearch = false)
        {
            pageNumber = parseInt(pageNumber);

            if(this.cPage !== pageNumber)
            {
                this.cPage = pageNumber;

                // Load search results for the new page
                this.searchReturns();
            }
            else if(forceSearch)
            {
                this.searchReturns();
            }
        },

        searchField_OnInput: debounce(function()
        {
            // Always reload results from page #1
            this.searching = true;
            this.setCurrentPage(1, true);
        }, 800),

        searchReturns()
        {
            this.resultsLoading = true;

            // If the current page number is greater than the number of pages
            // for the new page size, reset the cursor to page #1.
            if((this.cPage - 1) * this.pageSize > this.total)
            {
                this.cPage = 1;
            }

            const options = {
                offset: (this.cPage - 1) * this.pageSize,
                limit:  this.pageSize,
                query:  this.searchText,
            };

            return this.$store.dispatch('returns/searchReturns', options)
                .then(returns =>
                {
                    this.searching = false;
                    this.resultsLoading = false;

                    return returns;
                });
        },

        remove(oReturn)
        {
            this.$store.dispatch('returns/remove', {
                ret: oReturn,
            }).then(response =>
            {
                this.searchReturns();
                useToast().success(this.$t('return.delete_success'));
            }).catch(error =>
            {
                let msg = this.$t('common.errors.unknown');

                if(error.response.status == 409)
                {
                    msg = error.response.errors;
                }

                useToast().error(msg);
            });
        },
    },
};
</script>


<!-- /////////////////////////////////////////////////////////////////////////// STYLE -->

<style scoped>
@reference "#main.css";


.sl-page__tools
{
    @apply flex flex-wrap justify-between items-center mb-4;
}

.sl-search
{
    @apply relative;
}

.sl-search > .mdi-icon
{
    @apply absolute top-1.5 left-2.5 w-[22px] h-[22px] text-gray-400 pointer-events-none;
}

.sl-search__field
{
    @apply border border-gray-300 px-[34px] w-full h-8 text-primary-500 rounded-full appearance-none;
}

.sl-search__field:focus
{
    @apply outline-none;
}

.pagination
{
    @apply flex ml-auto select-none;
}

.pagination__page-size
{
    @apply cursor-pointer relative flex justify-between items-center border border-gray-300 pl-2 min-w-8 h-8 shrink-0 text-gray-500 text-sm;

    & > span:first-child
    {
    }

    & > .mdi-icon
    {
        @apply ml-1 w-5 h-5 shrink-0 text-gray-500;
    }

    & > select
    {
        @apply cursor-pointer absolute top-0 right-0 bottom-0 left-0 opacity-0;
    }
}

.pagination__page
{
    @apply cursor-pointer flex justify-center items-center ml-2 p-2 border border-primary-100 min-w-8 h-8 shrink-0 text-primary-500 text-sm
        hover:bg-primary-50;
}

.pagination__page--active
{
    @apply bg-primary-100;
}

.pagination__spacer
{
    height: 32px;
    min-width: 32px;
    @apply ml-2 flex items-center justify-center text-sm text-gray-500 p-2;
}

</style>
